/**
 * VÉRAULT — Client Proposal · Decision Center (Phase 5)
 *
 * Calm, modular client decisions. One summary per module, expandable
 * individual decision units, Approve All when eligible, contextual
 * revision prompts. Approval attaches to the exact published version —
 * it never books, charges, or contacts a supplier.
 */
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle2, ChevronDown, Loader2, MessageCircle, Plane, BedDouble,
  Compass, Car, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { Surface, PortalButton, api } from "../../../index";

const MODULE_ICONS = { flights: Plane, stays: BedDouble, experiences: Compass, transportation: Car };

const STATUS_META = {
  awaiting: { label: "Awaiting your review", tone: "text-[hsl(var(--foreground-muted))]" },
  approved: { label: "Approved", tone: "text-emerald-700" },
  revision_requested: { label: "With your advisor", tone: "text-amber-700" },
  reapproval_required: { label: "Updated — please review again", tone: "text-amber-700" },
};

const money = (cents) =>
  cents == null ? null : (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const usDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : null;

function RevisionPanel({ unit, prompts, tripId, onDone, onClose }) {
  const [reasons, setReasons] = React.useState([]);
  const [message, setMessage] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const send = async () => {
    if (!reasons.length && !message.trim()) {
      toast.error("Please tell us what you'd like changed.");
      return;
    }
    setSending(true);
    try {
      await api.post(`/client-portal/trips/${tripId}/decisions/${unit.unit_id}/request-revision`, { reasons, message: message.trim() || null });
      toast.success("Sent to your advisor — this recommendation is now being refined for you.");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send your request.");
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="mt-3 rounded-md border border-[hsl(var(--accent-gold))]/25 bg-[hsl(var(--accent-gold))]/6 p-4 space-y-3" data-testid={`decision-${unit.unit_id.slice(0, 8)}-revise-panel`}>
      <p className="font-serif text-[13.5px] text-[hsl(var(--foreground))]">What would you like adjusted?</p>
      <div className="flex flex-wrap gap-1.5">
        {prompts.map((r) => {
          const on = reasons.includes(r);
          return (
            <button
              key={r} type="button"
              onClick={() => setReasons(on ? reasons.filter((x) => x !== r) : [...reasons, r])}
              className={`px-2.5 py-1 rounded-full font-mono text-[10px] uppercase tracking-[0.12em] border transition-colors ${
                on ? "bg-[hsl(var(--accent-gold))]/15 border-[hsl(var(--accent-gold))]/60 text-[hsl(var(--foreground))]"
                   : "border-[rgba(0,0,0,0.12)] text-[hsl(var(--foreground-muted))] hover:border-[hsl(var(--accent-gold))]/40"}`}
              data-testid={`decision-${unit.unit_id.slice(0, 8)}-chip-${r.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              {r}
            </button>
          );
        })}
      </div>
      <textarea
        value={message} onChange={(e) => setMessage(e.target.value)} rows={2}
        placeholder="Anything else we should know — in your own words."
        className="w-full rounded-md border border-[rgba(0,0,0,0.1)] focus:border-[hsl(var(--accent-gold))] outline-none px-3 py-2 font-serif text-[13px] bg-white"
        data-testid={`decision-${unit.unit_id.slice(0, 8)}-revise-message`}
      />
      <div className="flex items-center justify-end gap-2">
        <PortalButton variant="ghost" size="sm" onClick={onClose}>Cancel</PortalButton>
        <PortalButton variant="secondary" size="sm" disabled={sending}
          icon={sending ? <Loader2 className="size-3.5 animate-spin" /> : <MessageCircle className="size-3.5" strokeWidth={1.5} />}
          onClick={send}
          data-testid={`decision-${unit.unit_id.slice(0, 8)}-revise-send`}
        >
          Send to my advisor
        </PortalButton>
      </div>
    </div>
  );
}

function UnitRow({ unit, prompts, tripId, readOnly, onChanged }) {
  const [revising, setRevising] = React.useState(false);
  const [approving, setApproving] = React.useState(false);
  const id8 = unit.unit_id.slice(0, 8);
  const meta = STATUS_META[unit.status] || STATUS_META.awaiting;
  const approve = async () => {
    setApproving(true);
    try {
      await api.post(`/client-portal/trips/${tripId}/decisions/${unit.unit_id}/approve`);
      toast.success(`${unit.title} approved — your advisor has been notified.`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save your approval.");
    } finally {
      setApproving(false);
    }
  };
  return (
    <div className="py-3 border-t border-[rgba(0,0,0,0.05)]" data-testid={`decision-unit-${id8}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <div className="flex-1 min-w-[180px]">
          <p className="font-serif text-[14.5px] text-[hsl(var(--foreground))]">{unit.title}</p>
          <p className="font-serif text-[12px] text-[hsl(var(--foreground-muted))]">
            {[unit.destination, usDate(unit.when), money(unit.price_cents)].filter(Boolean).join(" · ")}
          </p>
        </div>
        <span className={`font-mono text-[9.5px] uppercase tracking-[0.16em] ${meta.tone}`} data-testid={`decision-unit-${id8}-status`}>
          {unit.status === "approved" && <CheckCircle2 className="inline size-3 mr-1 -mt-0.5" strokeWidth={1.5} />}
          {meta.label}
          {unit.status === "approved" && unit.approved_at ? ` · ${usDate(unit.approved_at)}` : ""}
        </span>
        {!readOnly && unit.status !== "approved" && unit.status !== "revision_requested" && (
          <div className="flex items-center gap-2">
            <PortalButton variant="primary" size="sm" disabled={approving}
              icon={approving ? <Loader2 className="size-3.5 animate-spin" /> : null}
              onClick={approve} data-testid={`decision-unit-${id8}-approve`}>
              Approve
            </PortalButton>
            <PortalButton variant="ghost" size="sm" onClick={() => setRevising((v) => !v)} data-testid={`decision-unit-${id8}-revise`}>
              Request Revision
            </PortalButton>
          </div>
        )}
        {!readOnly && unit.status === "revision_requested" && (
          <PortalButton variant="secondary" size="sm" disabled={approving} onClick={approve} data-testid={`decision-unit-${id8}-approve-anyway`}>
            Approve as presented
          </PortalButton>
        )}
      </div>
      {unit.open_revision && (
        <p className="mt-1.5 font-accent italic text-[12px] text-amber-800" data-testid={`decision-unit-${id8}-revision-note`}>
          You asked: {[...(unit.open_revision.reasons || []), unit.open_revision.message].filter(Boolean).join(" · ")}
        </p>
      )}
      {revising && !readOnly && (
        <RevisionPanel unit={unit} prompts={prompts} tripId={tripId}
          onDone={() => { setRevising(false); onChanged(); }} onClose={() => setRevising(false)} />
      )}
    </div>
  );
}

function ModuleRow({ row, tripId, readOnly, onChanged }) {
  const [open, setOpen] = React.useState(false);
  const [approvingAll, setApprovingAll] = React.useState(false);
  const Icon = MODULE_ICONS[row.module] || Compass;
  const units = row.units.filter((u) => u.decision_required);
  if (!units.length) return null;
  const bits = [];
  if (row.approved) bits.push(`${row.approved} approved`);
  if (row.revision_requested) bits.push(`${row.revision_requested} with your advisor`);
  if (row.reapproval_required) bits.push(`${row.reapproval_required} updated for re-review`);
  if (row.awaiting) bits.push(`${row.awaiting} awaiting your review`);
  const approveAll = async () => {
    setApprovingAll(true);
    try {
      const { data } = await api.post(`/client-portal/trips/${tripId}/decisions/approve-all`, { module: row.module });
      toast.success(`${data.count} ${row.label.toLowerCase()} recommendation${data.count === 1 ? "" : "s"} approved.`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not approve.");
    } finally {
      setApprovingAll(false);
    }
  };
  return (
    <div className="px-4 sm:px-5 py-4" data-testid={`decision-module-${row.module}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-2.5 flex-1 min-w-[200px] text-left" data-testid={`decision-module-${row.module}-toggle`}>
          <Icon className="size-4 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
          <div>
            <p className="font-serif text-[15.5px] text-[hsl(var(--foreground))]">{row.label}</p>
            <p className="font-serif text-[12px] text-[hsl(var(--foreground-muted))]" data-testid={`decision-module-${row.module}-summary`}>
              {bits.join(" · ") || "Nothing needs your decision"}
            </p>
          </div>
        </button>
        {!readOnly && row.approve_all_eligible && (
          <PortalButton variant="secondary" size="sm" disabled={approvingAll}
            icon={approvingAll ? <Loader2 className="size-3.5 animate-spin" /> : <CheckCircle2 className="size-3.5" strokeWidth={1.5} />}
            onClick={approveAll} data-testid={`decision-module-${row.module}-approve-all`}>
            Approve All {row.label === "Experiences" ? "Required Experiences" : row.label === "Transportation" ? "Required Transportation" : `${row.label.replace(/s$/, "")} Recommendations`}
          </PortalButton>
        )}
        <button type="button" onClick={() => setOpen((v) => !v)} className="p-1" aria-label="Expand decisions">
          <ChevronDown className={`size-4 text-[hsl(var(--foreground-muted))] transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
            <div className="mt-2">
              {units.map((u) => (
                <UnitRow key={u.unit_id} unit={u} prompts={row.prompts} tripId={tripId} readOnly={readOnly} onChanged={onChanged} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function DecisionCenter({ tripId, readOnly = false }) {
  const [data, setData] = React.useState(null);
  const load = React.useCallback(() => {
    const path = readOnly
      ? `/design-studio/${tripId}/decisions/panel`
      : `/client-portal/trips/${tripId}/decisions`;
    api.get(path).then(({ data: d }) => setData(d)).catch(() => {});
  }, [tripId, readOnly]);
  React.useEffect(() => { load(); }, [load]);
  if (!data || !data.totals || data.totals.decisions === 0) return null;
  const t = data.totals;
  return (
    <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-3" data-testid="decision-center">
      <div className="flex items-center gap-2 text-[hsl(var(--accent-gold))]">
        <Clock className="size-3.5" strokeWidth={1.5} />
        <span className="font-mono text-[10px] uppercase tracking-[0.26em]">Your Decisions</span>
      </div>
      <Surface variant="default" className="!p-0 divide-y divide-[rgba(0,0,0,0.05)]">
        <div className="px-4 sm:px-5 py-4">
          <p className="font-serif text-[16px] text-[hsl(var(--foreground))]" data-testid="decision-center-totals">
            {t.decisions} decision{t.decisions === 1 ? "" : "s"}
            {" · "}{t.approved} approved
            {t.revision_requested ? ` · ${t.revision_requested} being refined` : ""}
            {t.reapproval_required ? ` · ${t.reapproval_required} updated for re-review` : ""}
            {" · "}{t.awaiting + (t.reapproval_required || 0)} awaiting you
          </p>
          <p className="mt-1 font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">
            Approve each part independently — nothing is booked or charged by approving. Your advisor handles every arrangement.
          </p>
        </div>
        {data.modules.map((row) => (
          <ModuleRow key={row.module} row={row} tripId={tripId} readOnly={readOnly} onChanged={load} />
        ))}
      </Surface>
    </motion.section>
  );
}
